/**
 * Mirrors backoffice subscription_plans into the main app `plans/{planId}` collection
 * so acctoo-app subscriptionLimits.js and usage surfaces read the same catalog.
 */
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ensureAppServiceSignIn, getAppFirestore } from '../appFirebaseClient';
import { VALID_PLAN_IDS } from '../constants/subscriptionContract';

const UNLIMITED = { limit: -1, unlimited: true };

/** Feature caps used by app enforcedTracking / subscriptionLimits (plans/{id}.features). */
export function derivePlanFeatures(planId) {
  const id = String(planId || 'starter').toLowerCase();
  if (id === 'enterprise' || id === 'max') {
    return {
      sales: {
        invoices: UNLIMITED,
        quotations: UNLIMITED,
        challans: UNLIMITED,
        creditNotes: UNLIMITED,
        debitNotes: UNLIMITED
      },
      purchase: {
        bills: UNLIMITED,
        purchaseOrders: UNLIMITED,
        receipts: UNLIMITED,
        creditNotes: UNLIMITED,
        debitNotes: UNLIMITED
      },
      payments: { receipts: UNLIMITED, payments: UNLIMITED, refunds: UNLIMITED },
      parties: { customers: UNLIMITED, suppliers: UNLIMITED, employees: UNLIMITED },
      reports: {
        gstReports: UNLIMITED,
        financialReports: UNLIMITED,
        customReports: UNLIMITED,
        exports: UNLIMITED
      },
      storage: {
        dataStorage: { limit: 0, unlimited: true, unit: 'GB' },
        fileUploads: { limit: 0, unlimited: true, unit: 'MB' }
      },
      users: { userLimit: UNLIMITED, companyLimit: UNLIMITED }
    };
  }
  if (id === 'pro') {
    return {
      sales: {
        invoices: { limit: 500, unlimited: false },
        quotations: { limit: 200, unlimited: false },
        challans: { limit: 200, unlimited: false },
        creditNotes: { limit: 100, unlimited: false },
        debitNotes: { limit: 100, unlimited: false }
      },
      purchase: {
        bills: { limit: 500, unlimited: false },
        purchaseOrders: { limit: 200, unlimited: false },
        receipts: { limit: 200, unlimited: false },
        creditNotes: { limit: 100, unlimited: false },
        debitNotes: { limit: 100, unlimited: false }
      },
      payments: {
        receipts: { limit: 1000, unlimited: false },
        payments: { limit: 1000, unlimited: false },
        refunds: { limit: 100, unlimited: false }
      },
      parties: {
        customers: { limit: 500, unlimited: false },
        suppliers: { limit: 500, unlimited: false },
        employees: { limit: 50, unlimited: false }
      },
      reports: {
        gstReports: { limit: 50, unlimited: false },
        financialReports: { limit: 50, unlimited: false },
        customReports: { limit: 20, unlimited: false },
        exports: { limit: 100, unlimited: false }
      },
      storage: {
        dataStorage: { limit: 5, unlimited: false, unit: 'GB' },
        fileUploads: { limit: 500, unlimited: false, unit: 'MB' }
      },
      users: { userLimit: { limit: 10, unlimited: false }, companyLimit: { limit: 3, unlimited: false } }
    };
  }
  if (id === 'basic' || id === 'starter') {
    return {
      sales: {
        invoices: { limit: 100, unlimited: false },
        quotations: { limit: 50, unlimited: false },
        challans: { limit: 50, unlimited: false },
        creditNotes: { limit: 25, unlimited: false },
        debitNotes: { limit: 25, unlimited: false }
      },
      purchase: {
        bills: { limit: 100, unlimited: false },
        purchaseOrders: { limit: 50, unlimited: false },
        receipts: { limit: 50, unlimited: false },
        creditNotes: { limit: 25, unlimited: false },
        debitNotes: { limit: 25, unlimited: false }
      },
      payments: {
        receipts: { limit: 200, unlimited: false },
        payments: { limit: 200, unlimited: false },
        refunds: { limit: 25, unlimited: false }
      },
      parties: {
        customers: { limit: 100, unlimited: false },
        suppliers: { limit: 100, unlimited: false },
        employees: { limit: 10, unlimited: false }
      },
      reports: {
        gstReports: { limit: 12, unlimited: false },
        financialReports: { limit: 12, unlimited: false },
        customReports: { limit: 5, unlimited: false },
        exports: { limit: 25, unlimited: false }
      },
      storage: {
        dataStorage: { limit: 1, unlimited: false, unit: 'GB' },
        fileUploads: { limit: 100, unlimited: false, unit: 'MB' }
      },
      users: { userLimit: { limit: 3, unlimited: false }, companyLimit: { limit: 1, unlimited: false } }
    };
  }
  return derivePlanFeatures('starter');
}

export function normalizeCatalogLimits(limits = {}) {
  return {
    firebase_operations: Number(limits.firebase_operations ?? 0),
    storage_mb: Number(limits.storage_mb ?? 0),
    sales_documents: Number(limits.sales_documents ?? 0),
    parties: Number(limits.parties ?? 0)
  };
}

/**
 * Write plan to app Firestore `plans/{planId}` (used by acctoo-app).
 */
export async function syncCatalogPlanToApp(planId, catalogPlan) {
  if (!VALID_PLAN_IDS.includes(planId)) {
    throw new Error(
      `Plan ID "${planId}" is not in the canonical set: ${VALID_PLAN_IDS.join(', ')}`
    );
  }

  await ensureAppServiceSignIn();
  const appDb = getAppFirestore();
  const limits = normalizeCatalogLimits(catalogPlan.limits || {});

  const appPlanDoc = {
    id: planId,
    name: catalogPlan.name || `${planId} Plan`,
    price: Number(catalogPlan.price ?? 0),
    currency: catalogPlan.currency || '₹',
    billing: catalogPlan.billing || 'monthly',
    description: catalogPlan.description || '',
    limits,
    features: catalogPlan.features || derivePlanFeatures(planId),
    isActive: catalogPlan.isActive !== false,
    catalogSource: 'subscription_plans',
    catalogSyncedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  await setDoc(doc(appDb, 'plans', planId), appPlanDoc, { merge: true });
  return appPlanDoc;
}

export async function syncAllCatalogPlansToApp(plansObject) {
  const entries = Object.entries(plansObject || {});
  for (const [planId, plan] of entries) {
    await syncCatalogPlanToApp(planId, plan);
  }
  return entries.length;
}
